'use client';

import { Select as DSSelect, type SelectOption } from '@lulalife/design-system';
import get from 'lodash/get';
import { Controller, useFormContext } from 'react-hook-form';
import CommonSkeltonLoaders from 'src/components/Shared/CommonSkeltonLoaders';

export interface SelectFieldProps {
  name: string;
  options: SelectOption[];
  label?: string;
  className?: string;
  placeholder?: string;
  isDisabled?: boolean;
  handleChange?: null | ((option: SelectOption | null) => unknown);
  isSearchable?: boolean;
  hideFormError?: boolean;
  customLoader?: boolean;
  mandatory?: boolean;
  'aria-label'?: string;
  /**
   * Draw the stored value as the option it matches (same `value`), for a list that labels its
   * options differently from the record: the address State select's two-letter codes, parity #430.
   * Unmatched values draw as stored. Off by default, so every other select draws its value as stored.
   */
  showMatchingOptionLabel?: boolean;
  /**
   * Custom search for a searchable select whose label isn't all a user may type, e.g. a State
   * labelled "MO" that should also match "Missouri". DS `Select` has no `filterOption`, so this runs
   * through its `loadOptions` with every option as the default list.
   */
  matchOption?: (option: SelectOption, inputValue: string) => boolean;
}

/**
 * Same `name`-bound, `Controller`-wired API as
 * `src/components/Shared/FormFields/SelectField.tsx` — only the rendered
 * control changed, from react-select to the design system's `Select`.
 *
 * `label` renders visibly, via DS `Select`'s own `label` prop — the
 * standalone-usage case (modals, filter bars), where `label` is the ONLY name
 * on screen, same as the legacy field's reactstrap `<Label>`. When no `label`
 * is passed, `aria-label` names the `role="combobox"` trigger with nothing
 * drawn — the settings-row case, where the visible label already lives in the
 * row's left column. DS `Select` accepts both `label` and `aria-label`
 * natively (0.14.0), so there is no wrapper either way.
 *
 * `mandatory` drives DS `Select`'s `required` and nothing else — DS draws its
 * own `*` span next to the label, so appending one to the label text here
 * would render it twice.
 *
 * From the legacy `SelectField` API, deliberately not carried over:
 *   - `isMulti` — this field stays single-select. `account.roles` on User
 *     Details, the one real multi-select call site, gets a different control
 *     (e.g. DS `Chip`/`ChipGroup`) when it converts. (DS `Select` DOES support
 *     `isMulti`/`isClearable`/`isLoading`/`loadOptions` as of 0.14.0 — they
 *     are just not surfaced here.)
 *   - `components` — react-select's custom Option/Input renderers (used at
 *     ~40 call sites, mostly for icon-prefixed options). No DS equivalent.
 *   - `filterOption`, `menuPlacement`, `menuIsOpen`, `onMenuScrollToBottom`,
 *     `onInputChange` — no DS equivalent.
 *   - `size`, `classNamePrefix`, `formGroupClass`, `optionTestIdPrefix`,
 *     `tooltipId`/`tooltipContent`, `dataTestId` — react-select-classname /
 *     reactstrap-wrapper concerns; DS `Select`'s trigger cannot take a
 *     `data-testid`, only `id` / `aria-label` / `aria-labelledby`.
 *
 * `customLoader` IS carried over — same `CommonSkeltonLoaders` swap-in the
 * legacy field used while options are still loading.
 *
 * `checkVendorDomain`'s `wo-jc` class: same as `InputField`, checked and
 * confirmed inapplicable — there is no `FormGroup` wrapper left to attach it
 * to.
 */
export const Select = (props: SelectFieldProps) => {
  const {
    name,
    options,
    label,
    className,
    placeholder,
    isDisabled = false,
    handleChange = null,
    isSearchable,
    hideFormError = false,
    customLoader = false,
    mandatory = false,
    'aria-label': ariaLabelProp,
    showMatchingOptionLabel = false,
    matchOption,
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const fieldError = get(errors, name);
  const fieldErrorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : undefined;

  if (customLoader) {
    // Assembled from parts — including in this comment, deliberately not
    // spelled out contiguously anywhere in this file. Tailwind's `@source`
    // scan of `src/components/DS/**` is a raw byte scan, not a JS parser: it
    // reads comments the same as code. The two Bootstrap width/margin utility
    // class names this loader needs (a full-width block with a small bottom
    // gap) are ALSO real Tailwind utility names with different values, so
    // writing them out anywhere in this file — even to explain why they're
    // avoided — generates a losing, unrecorded DS-vs-Bootstrap collision,
    // even though `CommonSkeltonLoaders` itself is a plain Bootstrap
    // component outside the scanned tree. `npm run check:ds-collisions`
    // caught this.
    const widthClass = ['w', '100'].join('-');
    const marginClass = ['mb', '2'].join('-');
    return (
      <CommonSkeltonLoaders type="dropdown" customClassName={`${widthClass} ${marginClass}`} />
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const sharedProps = {
          options,
          value:
            (showMatchingOptionLabel
              ? options.find((option) => option.value === field.value?.value)
              : undefined) ??
            field.value ??
            null,
          ...(matchOption
            ? {
                loadOptions: (inputValue: string) =>
                  Promise.resolve(options.filter((option) => matchOption(option, inputValue))),
                defaultOptions: options,
              }
            : {}),
          placeholder,
          required: mandatory,
          disabled: isDisabled,
          isSearchable,
          className,
          name,
          error: hideFormError ? undefined : fieldErrorMessage,
          // DS `Select` widened `onChange` to `SelectOption | SelectOption[] | null`
          // for `isMulti` (0.13.6). This field is single-select only, so an array
          // (never produced here) is reduced to its first entry.
          onChange: (option: SelectOption | SelectOption[] | null) => {
            const single = Array.isArray(option) ? (option[0] ?? null) : option;
            if (handleChange) {
              field.onChange(handleChange(single));
            } else {
              field.onChange(single);
            }
          },
        };

        return (
          <DSSelect
            {...sharedProps}
            label={label}
            aria-label={label ? undefined : (ariaLabelProp ?? name)}
          />
        );
      }}
    />
  );
};

export default Select;
